'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Box, Chip, FormControl, Select, MenuItem, Stack, Typography, IconButton, Tooltip } from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { readStoredOwnerAccountId, useRBACOptional } from '@/contexts/RBACContext';
import { getUser, listAccountUsers, listAllAccounts } from '@/services/rbacApi';
import { RBACApiError } from '@/services/rbacApi';
import type { Account } from '@/types/rbac';

type SubTenantMemberOption = {
  rbacUserId: number;
  externalUserId: number;
  label: string;
};

/** Sentinel value used in the <Select> to represent the "Organization" (root account) context. */
const ROOT_SENTINEL = '__root__';

export function SubAccountSelector() {
  const router = useRouter();
  const rbac = useRBACOptional();
  const currentOrgId = rbac?.currentOrgId ?? null;
  const currentAccountId = rbac?.currentAccountId ?? null;
  const defaultAccountId = rbac?.defaultAccountId ?? null;
  const isRBACLoading = rbac?.isLoading ?? false;
  const selectedSubAccountId = rbac?.selectedSubAccountId ?? null;
  const selectedSubTenantMemberExternalUserId =
    rbac?.selectedSubTenantMemberExternalUserId ?? null;
  const setSelectedSubAccountId = rbac?.setSelectedSubAccountId;
  const setSelectedSubTenantMemberExternalUserId =
    rbac?.setSelectedSubTenantMemberExternalUserId;
  const setCurrentAccountId = rbac?.setCurrentAccountId;
  // Only the actual org owner (backend is_org_owner flag) gets the full switcher.
  // Users with the org_owner role code but who are not the true owner only see the read-only badge.
  const isOrgOwner = Boolean(rbac?.userContext?.is_org_owner);

  // Root context state is persisted in RBACContext (localStorage-backed) so it survives
  // page refreshes and component remounts without resetting the dropdown to "Select Context".
  const isRootContextActive = rbac?.isRootContextActive ?? false;
  const setIsRootContextActive = rbac?.setIsRootContextActive;

  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [members, setMembers] = useState<SubTenantMemberOption[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  // Fetch all accounts for the org (owners see all; non-owners see their own)
  useEffect(() => {
    if (isRBACLoading || !currentOrgId) {
      setAllAccounts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setIsLoadingAccounts(true);
        const list = await listAllAccounts(currentOrgId);
        if (!cancelled) setAllAccounts(list);
      } catch (err) {
        if (!cancelled && !(err instanceof RBACApiError)) {
          console.error('Failed to fetch accounts:', err);
        }
        if (!cancelled) setAllAccounts([]);
      } finally {
        if (!cancelled) setIsLoadingAccounts(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentOrgId, isRBACLoading]);

  // Root account: parent_account_id === null or account_type === 'root'
  const rootAccount = allAccounts.find(
    (a) => a.parent_account_id == null || a.account_type === 'root'
  ) ?? null;

  // Sub-accounts for the owner switcher: everything except the root account
  const subAccounts = rootAccount
    ? allAccounts.filter((a) => a.id !== rootAccount.id)
    : allAccounts.filter((a) => a.id !== defaultAccountId);

  // Determine the account to load members from:
  //   - sub-account context: use selectedSubAccountId
  //   - root org context or nothing picked yet: use rootAccount.id / defaultAccountId (org-wide aggregate)
  // Computed outside the effect below and used as its dependency (instead of the three raw
  // inputs) so the effect only re-fetches when the *resolved* target actually changes — e.g.
  // `rootAccount?.id` flips from undefined to a real id once `allAccounts` loads, but that's
  // frequently the same account `defaultAccountId` already pointed at, which was causing the
  // whole members fetch (list + per-member fallback calls) to fire twice on every page load.
  const targetAccountId = selectedSubAccountId ?? (rootAccount?.id ?? defaultAccountId);

  // Load members for the selected account context (owner only).
  // Works for root org context (isRootContextActive), the default unpicked state (which
  // aggregates org-wide the same as root context, see RBACContext's effectiveListingSubAccountId),
  // and sub-account context.
  useEffect(() => {
    if (!isOrgOwner || isRBACLoading || !currentOrgId || !targetAccountId) {
      setMembers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setIsLoadingMembers(true);
        const resp = await listAccountUsers(targetAccountId, true, currentOrgId, true);
        const rows = resp.users || [];
        // external_user_id/display_name/first_name/last_name/email now come straight from this
        // list response (rbac-svc includes them) — only fall back to a per-user GET for a row
        // missing external_user_id, instead of doing it unconditionally for every member.
        const hydrated = await Promise.all(
          rows.map(async (row) => {
            const rbacUserId = Number(row.user_id ?? row.userid);
            if (!Number.isFinite(rbacUserId)) return null;
            try {
              let externalUserId = Number(row.external_user_id);
              let displayName = row.display_name;
              let firstName = row.first_name;
              let lastName = row.last_name;
              let email = row.email;
              // external_user_id is legitimately negative for many real rows — only 0/missing
              // (rbac-svc's "unset" sentinel) means the list response didn't already have it.
              if (!Number.isFinite(externalUserId) || externalUserId === 0) {
                const user = await getUser(rbacUserId);
                externalUserId = Number(user.external_user_id);
                displayName = user.display_name;
                firstName = user.first_name;
                lastName = user.last_name;
                email = user.email;
              }
              if (!Number.isFinite(externalUserId) || externalUserId === 0) return null;
              const label =
                displayName?.trim() ||
                [firstName, lastName].filter(Boolean).join(' ').trim() ||
                email ||
                row.email ||
                `Member ${externalUserId}`;
              return { rbacUserId, externalUserId, label };
            } catch {
              return null;
            }
          })
        );
        if (!cancelled) {
          const unique = Array.from(
            new Map(
              hydrated
                .filter((row): row is SubTenantMemberOption => row !== null)
                .map((row) => [row.externalUserId, row])
            ).values()
          );
          setMembers(unique);
        }
      } catch (err) {
        if (!cancelled && !(err instanceof RBACApiError)) {
          console.error('Failed to fetch account members:', err);
        }
        if (!cancelled) setMembers([]);
      } finally {
        if (!cancelled) setIsLoadingMembers(false);
      }
    })();
    return () => { cancelled = true; };
    // isRootContextActive intentionally excluded — not read inside this effect (targetAccountId
    // already accounts for the root/sub-account distinction); including it caused an extra
    // needless re-run whenever it changed without the actual target account changing.
  }, [currentOrgId, isRBACLoading, isOrgOwner, targetAccountId]);

  if (!rbac) return null;

  // ── Non-owner: show read-only context badge ──────────────────────────────
  if (!isOrgOwner) {
    const userAccountId = rbac.userContext?.account_id ?? null;
    const isRootMember = userAccountId != null && rootAccount != null && userAccountId === rootAccount.id;
    const contextLabel = isLoadingAccounts
      ? 'Loading...'
      : isRootMember
        ? 'Organization'
        : allAccounts.find((a) => a.id === userAccountId)?.name ?? 'My Account';
    const icon = isRootMember ? 'mdi:office-building' : 'mdi:account-group';

    return (
      <Chip
        size="small"
        icon={<Iconify icon={icon} width={14} />}
        label={
          <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: 120 }}>
            {contextLabel}
          </Typography>
        }
        sx={{
          height: 28,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          '& .MuiChip-label': { px: 1 },
        }}
      />
    );
  }

  // ── Owner: full switcher ─────────────────────────────────────────────────
  const rootId = rootAccount?.id ?? defaultAccountId;
  // isRootContextActive is the canonical signal for root org context (we no longer set
  // selectedSubAccountId=rootId, to avoid polluting effectiveScopedAccountId in RBACContext).
  const isRootSelected = isRootContextActive;
  const isSubSelected =
    !isRootContextActive && selectedSubAccountId != null && selectedSubAccountId !== rootId;
  const activeTenantId = isSubSelected ? selectedSubAccountId : null;
  const subTenant = subAccounts.find((sa) => sa.id === activeTenantId);

  // Derive the Select value
  const selectValue = isRootSelected
    ? ROOT_SENTINEL
    : activeTenantId != null
      ? String(activeTenantId)
      : '';

  // Nothing picked behaves the same as explicitly picking "Organization" (both aggregate org-wide
  // data, see RBACContext's effectiveListingSubAccountId), so show the same label/icon for both —
  // otherwise the selector would look unchanged while the numbers underneath were actually scoped
  // differently.
  const displayLabel = isLoadingAccounts || isRBACLoading
    ? 'Loading...'
    : activeTenantId == null
      ? rbac.userContext?.org_name ?? 'Organization'
      : subTenant?.name ?? 'Select Context';

  const handleChange = (e: { target: { value: string } }) => {
    const v = e.target.value;
    if (v === ROOT_SENTINEL) {
      // Switch to Organization (root) context.
      // Use selectedSubAccountId=null so effectiveScopedAccountId in RBACContext stays null,
      // preserving the org_owner permission bypass (prevents the app menu from disappearing).
      // This only applies to the "all members" root view — once a specific member is picked via
      // handleMemberChange, RBACContext resolves effectiveScopedAccountId to that member's scope.
      // Root context is tracked in RBACContext (localStorage-backed) so it persists across refreshes.
      setSelectedSubAccountId?.(null);
      setSelectedSubTenantMemberExternalUserId?.(null);
      if (rootId != null) setCurrentAccountId?.(rootId);
      setIsRootContextActive?.(true);
      router.replace('/dashboard');
      return;
    }
    const id = Number(v);
    if (!Number.isFinite(id)) return;
    setIsRootContextActive?.(false);
    setSelectedSubAccountId?.(id);
    setSelectedSubTenantMemberExternalUserId?.(null);
    setCurrentAccountId?.(id);
    router.replace('/dashboard');
  };

  const handleMemberChange = (e: { target: { value: string } }) => {
    const v = e.target.value;
    if (v === '') {
      setSelectedSubTenantMemberExternalUserId?.(null);
      router.replace('/dashboard');
      return;
    }
    const id = Number(v);
    if (!Number.isFinite(id)) return;
    const member = members.find((m) => m.externalUserId === id);
    setSelectedSubTenantMemberExternalUserId?.(id, member?.rbacUserId ?? null);
    router.replace('/dashboard');
  };

  const resolveOwnerAccountId = (): number | null => {
    if (defaultAccountId != null && defaultAccountId > 0) return defaultAccountId;
    const ctx = rbac.userContext?.account_id;
    if (ctx != null && ctx > 0) return ctx;
    const stored = readStoredOwnerAccountId();
    if (stored != null) return stored;
    // Pure org owners can have a null account_id in /me/context (they aren't tied to one
    // account), so none of the above ever get populated. Fall back to the root account
    // resolved from listAllAccounts — the same id the "Organization" menu option uses —
    // so the reset button still has somewhere valid to send currentAccountId instead of
    // silently no-op'ing and leaving the sub-account scope in place.
    return rootId;
  };

  const handleResetToOwner = () => {
    const ownerId = resolveOwnerAccountId();
    setIsRootContextActive?.(false);
    setSelectedSubAccountId?.(null);
    setSelectedSubTenantMemberExternalUserId?.(null);
    if (ownerId != null) setCurrentAccountId?.(ownerId);
    router.replace('/dashboard');
  };

  // Show member picker whenever there's an account to list members from: root org context,
  // sub-account context, or the default unpicked state (which aggregates org-wide the same as
  // root context — see RBACContext's effectiveListingSubAccountId — so it needs the same ability
  // to drill into one member). Mirrors the targetAccountId resolution in the members-loading effect.
  const showMemberPicker = (selectedSubAccountId ?? rootId) != null;
  // "Organization" alone (root context, All Members) is the owner's default view, so it gets
  // no reset button. Once a specific member is picked — even while still in the Organization
  // context — or a sub-account is selected, show the reset button to get back to that default.
  const showResetButton = isSubSelected || selectedSubTenantMemberExternalUserId != null;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <FormControl size="small" sx={{ minWidth: 170 }}>
        <Select
          value={selectValue}
          onChange={handleChange}
          disabled={isRBACLoading || isLoadingAccounts || !currentOrgId}
          displayEmpty
          renderValue={() => (
            <Stack direction="row" spacing={1} alignItems="center">
              <Iconify
                icon={activeTenantId == null ? 'mdi:office-building' : 'mdi:account-group'}
                width={18}
                sx={{ color: 'text.primary' }}
              />
              <Typography variant="body2" fontWeight={600} color="text.primary" noWrap sx={{ maxWidth: 120 }}>
                {displayLabel}
              </Typography>
            </Stack>
          )}
          sx={{
            height: 36,
            color: 'text.primary',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'text.primary' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1 },
          }}
        >
          {isLoadingAccounts ? (
            <MenuItem disabled>
              <Typography variant="body2" color="text.primary">Loading...</Typography>
            </MenuItem>
          ) : (
            [
              // "Organization" (root account) — always first
              rootId != null && (
                <MenuItem key="__root__" value={ROOT_SENTINEL}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon="mdi:office-building" width={18} sx={{ color: 'text.primary' }} />
                    <Typography variant="body2">
                      {rbac.userContext?.org_name ?? 'Organization'}
                    </Typography>
                  </Stack>
                </MenuItem>
              ),
              // Sub-accounts
              ...subAccounts.map((sa) => (
                <MenuItem key={sa.id} value={String(sa.id)}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon="mdi:account-group" width={18} sx={{ color: 'text.primary' }} />
                    <Typography variant="body2">{sa.name}</Typography>
                  </Stack>
                </MenuItem>
              )),
              // Empty state
              ...(subAccounts.length === 0 && rootId == null
                ? [
                  <MenuItem key="__empty__" disabled>
                    <Typography variant="body2" color="text.primary">
                      No accounts available
                    </Typography>
                  </MenuItem>,
                ]
                : []),
            ].filter(Boolean)
          )}
        </Select>
      </FormControl>

      {showMemberPicker && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={
              selectedSubTenantMemberExternalUserId != null
                ? String(selectedSubTenantMemberExternalUserId)
                : ''
            }
            onChange={handleMemberChange}
            disabled={isLoadingMembers}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon="mdi:account" width={18} sx={{ color: 'text.primary' }} />
                    <Typography variant="body2" fontWeight={600} color="text.primary" noWrap sx={{ maxWidth: 140 }}>
                      {isLoadingMembers ? 'Loading members...' : 'All Members'}
                    </Typography>
                  </Stack>
                );
              }
              const member = members.find((m) => String(m.externalUserId) === String(selected));
              return (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Iconify icon="mdi:account" width={18} sx={{ color: 'text.primary' }} />
                  <Typography variant="body2" fontWeight={600} color="text.primary" noWrap sx={{ maxWidth: 140 }}>
                    {member?.label ?? 'Selected Member'}
                  </Typography>
                </Stack>
              );
            }}
            sx={{
              height: 36,
              color: 'text.primary',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'text.primary' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1 },
            }}
          >
            <MenuItem value="">
              <Typography variant="body2">All Members</Typography>
            </MenuItem>
            {members.map((member) => (
              <MenuItem key={member.externalUserId} value={String(member.externalUserId)}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Iconify icon="mdi:account" width={18} sx={{ color: 'text.primary' }} />
                  <Typography variant="body2">{member.label}</Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {showResetButton && (
        <Tooltip title="Reset to owner permissions">
          <Box
            component="span"
            sx={{ display: 'inline-flex', position: 'relative', zIndex: 2, alignItems: 'center' }}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleResetToOwner();
              }}
              sx={{
                width: 28,
                height: 28,
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.primary',
                pointerEvents: 'auto',
                '&:hover': { color: 'text.primary', borderColor: 'text.primary' },
              }}
            >
              <Iconify icon="mdi:backup-restore" width={16} />
            </IconButton>
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
}
